/**
 * "Open on your phone (Expo Go)" for an Expo service in the Preview tab: a preview link that opens only this service's
 * Metro on a host of its own (vfs-service src/access/expoGo.js), shown as a QR code and an exps:// address. Expo Go
 * cannot send Flow's login, so whoever has the link can open the app until it expires or is revoked here.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, QRCode } from 'antd';
import { DeviceMobile } from '@phosphor-icons/react';
import { createExpoGoLink, revokePreviewLink } from '../../utils/vfsClient';
import './ExpoGo.css';

/** Whether a preview is a React Native one (the runner's stack id or label): the only ones Expo Go may open. */
export function isReactNativePreview({ language, framework } = {}) {
  return language === 'react-native' || framework === 'React Native';
}

const whenText = (iso) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? 'it expires' : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export function ExpoGoDialog({ open, onClose, projectId, service }) {
  const [state, setState] = useState({ phase: 'loading', link: null, error: null });
  const [copied, setCopied] = useState(false);

  const make = useCallback(() => {
    let live = true;
    setState({ phase: 'loading', link: null, error: null });
    setCopied(false);
    createExpoGoLink(projectId, service)
      .then((link) => { if (live) { setState({ phase: 'ready', link, error: null }); } })
      .catch((error) => { if (live) { setState({ phase: 'error', link: null, error }); } });
    return () => { live = false; };
  }, [projectId, service]);

  useEffect(() => (open ? make() : undefined), [open, make]);

  const revoke = async() => {
    const { link } = state;
    setState((s) => ({ ...s, phase: 'revoking' }));
    try {
      await revokePreviewLink(projectId, link.id);
      setState({ phase: 'revoked', link: null, error: null });
    } catch (error) {
      setState({ phase: 'ready', link, error });
    }
  };

  const copy = async() => {
    try {
      await navigator.clipboard.writeText(state.link.expoUrl);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const { phase, link, error } = state;
  return (
    <Modal destroyOnHidden footer={null} onCancel={onClose} open={open} title="Open on your phone (Expo Go)" width={440} zIndex={1200}>
      <div className="expogo" data-testid="expogo-dialog">
        {phase === 'loading' && <p className="expogo-muted">Making the link…</p>}
        {phase === 'error' && (
          <>
            <p className="expogo-bad" data-testid="expogo-error" role="alert">{error?.message || 'The link could not be made.'}</p>
            <Button data-testid="expogo-retry" onClick={make}>Try again</Button>
          </>
        )}
        {phase === 'revoked' && (
          <>
            <p data-testid="expogo-revoked">Revoked. The link no longer opens the app.</p>
            <Button data-testid="expogo-new" onClick={make} type="primary">Make a new link</Button>
          </>
        )}
        {(phase === 'ready' || phase === 'revoking') && link && (
          <>
            <ol className="expogo-steps">
              <li>Install <strong>Expo Go</strong> on the phone (App Store or Google Play).</li>
              <li>Scan this code: with the Camera app on an iPhone, from Expo Go on Android.</li>
            </ol>
            <QRCode data-testid="expogo-qr" size={220} type="svg" value={link.expoUrl} />
            <code className="expogo-url" data-testid="expogo-url">{link.expoUrl}</code>
            <div className="expogo-actions">
              <Button data-testid="expogo-copy" onClick={copy} size="small">{copied ? 'Copied' : 'Copy link'}</Button>
              <Button danger data-testid="expogo-revoke" loading={phase === 'revoking'} onClick={revoke} size="small">Revoke</Button>
            </div>
            <p className="expogo-note" data-testid="expogo-note">
              Anyone with this link can open this preview on a phone, without signing in to Flow, until {whenText(link.expiresAt)} or until you revoke it.
              Keep the service running here while you use it; your changes reload on the phone.
            </p>
            {error && <p className="expogo-bad" role="alert">{error.message || 'The link could not be revoked.'}</p>}
          </>
        )}
      </div>
    </Modal>
  );
}

ExpoGoDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  projectId: PropTypes.string.isRequired,
  service: PropTypes.string,
};

/** The Preview tab's button: only for a React Native preview of a project. */
export default function ExpoGoButton({ projectId, service, language, framework, className }) {
  const [open, setOpen] = useState(false);
  if (!projectId || !isReactNativePreview({ language, framework })) { return null; }
  return (
    <>
      <button
        aria-label="Open on your phone (Expo Go)"
        className={className || 'nav-btn'}
        data-testid="expogo-open"
        onClick={() => setOpen(true)}
        title="Open on your phone (Expo Go)"
        type="button"
      >
        <DeviceMobile aria-hidden size={13} />
      </button>
      {open && <ExpoGoDialog onClose={() => setOpen(false)} open projectId={projectId} service={service} />}
    </>
  );
}

ExpoGoButton.propTypes = {
  className: PropTypes.string,
  framework: PropTypes.string,
  language: PropTypes.string,
  projectId: PropTypes.string,
  service: PropTypes.string,
};
